import React, { useState, useEffect } from "react";
import SEO from "../seo";
const UsersAndPermissions = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "team-members",
        "invite-team-member",
        "access-levels",
        "default-full-access",
        "custom-permissions",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Users & Permissions | xPage Help Center"
        description="Learn how to manage Users & Permissions in xPage. Add team members, assign roles, customize permissions, manage access levels, and ensure secure store operation across your team."
        keywords="xPage users, xPage permissions, team members, roles, access levels, custom permissions, manage team, store access, user roles, invite team member"
        canonical="https://help.xpage.ai/help/settings/Users%20%26%20Permissions"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Users & Permissions
          </h1>
          <p className="text-sm text-systemLabel">
            The Users & Permissions section in Xpage allows you to manage your
            team, control access levels, and ensure that the right people have
            the appropriate permissions to operate your store securely and
            efficiently. This section is critical for maintaining operational
            integrity while enabling collaboration across your team.
          </p>
        </header>

        {/* Team Members Section */}
        <section id="team-members" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Team Members
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Active Members
                </h3>
                <p className="text-sm text-systemLabel">
                  View a list of active members currently granted access to your
                  store.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Administrator Role
                </h3>
                <p className="text-sm text-systemLabel">
                  The Administrator is the default role and has full control,
                  including the ability to transfer store ownership if
                  necessary.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Pending Invitations
                </h3>
                <p className="text-sm text-systemLabel">
                  Track pending invitations sent to new team members who have
                  not yet accepted access.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Invite Team Member Section */}
        <section id="invite-team-member" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Invite Team Member
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Sending Invitations
                </h3>
                <p className="text-sm text-systemLabel">
                  Send an invitation to add a new team member to your store.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Assigning Roles
                </h3>
                <p className="text-sm text-systemLabel">
                  Assign default roles or customize permissions to match the
                  responsibilities of each member.
                </p>
              </div>

              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Monitoring Invitations
                </h3>
                <p className="text-sm text-systemLabel">
                  Invitations can be monitored and revoked as needed.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Access Levels Section */}
        <section id="access-levels" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Access Levels
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">
              {/* Default Full Access */}
              <div id="default-full-access" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Default Full Access
                </h3>
                <p className="text-sm text-systemLabel">
                  Grant full access to all sections and features of the store
                  for trusted team members.
                </p>
                <p className="text-sm text-systemLabel mt-2">
                  Full access includes administrative actions, store settings,
                  and financial information.
                </p>
              </div>

              {/* Custom Permissions */}
              <div id="custom-permissions" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Custom Permissions
                </h3>
                <p className="text-sm text-systemLabel mb-3">
                  Tailor access for each team member by selecting specific
                  modules they can interact with:
                </p>

                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Permission
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        <strong className="text-systemTitle text-sm">
                          Description
                        </strong>
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Settings
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Control over store configuration
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Product
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Manage product creation and updates
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Policy
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Create or edit store policies
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Shipping
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Configure shipping rules and zones
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Payment Method
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Manage payment gateways and methods
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        View and manage customer orders
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Customer
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Access customer information and profiles
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Analytics
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        View store performance metrics
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Domain
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Manage store domains and URLs
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Landing Page
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Create or edit landing pages
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Bundle
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Configure product bundles and promotions
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Review
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Approve, edit, or respond to customer reviews
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Plan
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        View and modify subscription plans
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Invoice
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Access billing and transaction history
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          File
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Manage uploaded assets and media
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                By carefully managing Users & Permissions, you ensure
                operational security, limit unnecessary access, and empower your
                team members to perform their roles efficiently without
                compromising sensitive information.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#team-members"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "team-members"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Team Members
              </a>
            </li>
            <li>
              <a
                href="#invite-team-member"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "invite-team-member"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Invite Team Member
              </a>
            </li>
            <li>
              <a
                href="#access-levels"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "access-levels"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Access Levels
              </a>
            </li>
            <li>
              <a
                href="#default-full-access"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "default-full-access"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Default Full Access
              </a>
            </li>
            <li>
              <a
                href="#custom-permissions"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "custom-permissions"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Custom Permissions
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UsersAndPermissions;
